import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import NumberField from '../fields/NumberField';
import { auctioneerDataMatrixItemsType } from '../../types';
import { auctioneerLineKey, sortAuctioneerItems, totalVolumeOf } from '../../utils/volumeDistribution';

/**
 * The auctioneer break down of one target auction, shown in the expandable area under its row.
 *
 * The volume of an auctioneer has to be redistributed to the very same auctioneer, so every line of
 * the auction is editable on its own. A line is identified by auctioneer and submarket, because one
 * auctioneer may hold volume in more than one submarket; the lines are grouped by auctioneer so
 * that a break down with a single submarket per auctioneer reads as a plain auctioneer list.
 */
DistributeVolumeAuctioneerRows.propTypes = {
    auctionId: PropTypes.number.isRequired,
    items: auctioneerDataMatrixItemsType.isRequired,
    volumes: PropTypes.objectOf(PropTypes.string).isRequired,
    onVolumeChange: PropTypes.func.isRequired,
    validator: PropTypes.func.isRequired,
    step: PropTypes.number.isRequired,
    columnCount: PropTypes.number.isRequired,
    allocationOf: PropTypes.func.isRequired,
    cancelledVolumeOf: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

function DistributeVolumeAuctioneerRows(props) {
    const {
        auctionId,
        items,
        volumes,
        onVolumeChange,
        validator,
        step,
        columnCount,
        allocationOf,
        cancelledVolumeOf,
        disabled = false,
    } = props;
    const { t } = useTranslation();
    const sortedItems = sortAuctioneerItems(items);
    return (
        <tr className="distribute-volume-modal--auctioneers-row">
            <td colSpan={columnCount}>
                <table className="distribute-volume-modal--auctioneers-table">
                    <thead>
                        <tr>
                            <th>{t('auctions.distribute-volume.column.auctioneer')}</th>
                            <th>{t('auctions.distribute-volume.column.submarket')}</th>
                            <th>{t('auctions.distribute-volume.column.original-volume')}</th>
                            <th>{t('auctions.distribute-volume.column.distributed-volume')}</th>
                            <th>{t('auctions.distribute-volume.column.allocated-volume')}</th>
                            <th>{t('auctions.distribute-volume.column.total-volume')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedItems.map((item, index) => {
                            const { memberId, name } = item.auctioneer;
                            const key = auctioneerLineKey(auctionId, memberId, item.submarket.code);
                            // The auctioneer is named once per group so that its submarket lines
                            // read as belonging together.
                            const isFirstOfAuctioneer = index === 0
                                || sortedItems[index - 1].auctioneer.memberId !== memberId;
                            // What this auctioneer is being given in total, against what it held on
                            // the cancelled auction. The pair is the same on every auction the
                            // volume is distributed to, because it is the auctioneer as a whole
                            // that has to come out even, not its share of any single auction.
                            const { allocated } = allocationOf(item);
                            const cancelled = cancelledVolumeOf(item);
                            return (
                                <tr key={key} className="distribute-volume-modal--auctioneer-line">
                                    <td className="distribute-volume-modal--auctioneer-name">
                                        {isFirstOfAuctioneer ? `${name} (${memberId})` : ''}
                                    </td>
                                    <td className="distribute-volume-modal--auctioneer-submarket">
                                        {item.submarket.code}
                                    </td>
                                    <td className="distribute-volume-modal--auctioneer-original-volume">
                                        {item.quantity}
                                    </td>
                                    <td className="distribute-volume-modal--auctioneer-volume">
                                        <NumberField
                                            className="distribute-volume-modal--auctioneer-volume-input"
                                            inputId={`distribute-volume-modal--auctioneer-volume-input-${key}`}
                                            value={volumes[key] ?? ''}
                                            onChange={(newValue) => onVolumeChange(key, newValue)}
                                            validator={validator}
                                            step={step}
                                            min={0}
                                            fractionDigits={0}
                                            noThousandSeparator
                                            roundToStepMultiple
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td className={allocated === cancelled
                                        ? 'distribute-volume-modal--auctioneer-allocated-volume'
                                        : 'distribute-volume-modal--auctioneer-allocated-volume distribute-volume-modal--auctioneer-allocated-volume--mismatch'}
                                    >
                                        {`${allocated}/${cancelled}`}
                                    </td>
                                    <td className="distribute-volume-modal--auctioneer-total-volume">
                                        {totalVolumeOf(item.quantity, volumes[key])}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </td>
        </tr>
    );
}

export default DistributeVolumeAuctioneerRows;
